import { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '@/components/sidebar';
import { Dashboard } from '@/components/dashboard';
import { Meetings } from '@/components/meetings';
import { ActionItemsPage } from '@/components/action-items';
import { ActionItemBacklogBanner } from '@/components/action-item-backlog-banner';
import { Integrations } from '@/components/integrations';
import { SettingsNavigation } from '@/components/settings-navigation';
import { Auth } from '@/components/auth';
import { WorkspaceSelect } from '@/components/workspace-select';
import { AcceptInvitation } from '@/components/accept-invitation';
import { PasswordReset } from '@/components/password-reset';
import { MfaChallenge } from '@/components/mfa-challenge';
import {
  WorkspaceProvider,
  useWorkspace,
  getLastActiveWorkspace,
  useInvitations,
  updateLastActiveWorkspace,
} from '@/contexts/context';
import { useAuth } from '@/contexts/auth-context';
import { ensureCalendarConnectionFromAuth } from '@/lib/calendar-connection';
import type { View } from '@/lib/auth-state-machine';
import { Toaster } from '@/components/ui/sonner';
import { WorkspaceSearchProvider } from '@/components/workspace-search';
import { AgentChatProvider } from '@/components/agent-chat';
import { toast } from 'sonner';
import { supabase } from '@saas-boilerplate/shared';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useTrialStatus } from '@/hooks/use-trial-status';
import { WorkspaceOnboardingWizard } from '@/components/workspace-onboarding-wizard';
import { isWorkspaceSetupComplete } from '@/lib/workspace-setup';
import { AttendeeGate } from '@/components/attendee-gate';
import { AttendeeAllMeetings } from '@/components/attendee-all-meetings';
import { UnsubscribePage } from '@/components/unsubscribe-page';
import { DesignSystemPage } from '@/components/design-system-page';
import { ApproveRecapPage } from '@/components/approve-recap-page';
import { AddAgendaPage } from '@/components/add-agenda-page';
import { ActionItemDonePage } from '@/components/action-item-done-page';
import { ActionItemUpdatePage } from '@/components/action-item-update-page';
import { OAuthConsentPage } from '@/components/oauth-consent-page';
import { ReleasesPage } from '@/components/releases-page';
import { FeedbackPage } from '@/components/feedback-page';
import { AdminDashboard } from '@/components/admin-dashboard';

/** admin.upstand.ai serves the same SPA; the host pins the app to the staff admin dashboard. */
const isAdminHost = () =>
  typeof window !== 'undefined' && window.location.hostname.split('.')[0] === 'admin';

const PENDING_ATTENDEE_SLUG_KEY = 'upstand:pending_attendee_slug';

function AppContent({
  onSwitchWorkspace,
  onSignOut,
}: {
  onSwitchWorkspace: () => void;
  onSignOut: () => void;
}) {
  // Initialise the view from the URL so a deep-link (e.g. /meetings?meeting=…) doesn't transiently
  // mount the Dashboard during boot - that brief mount was firing the dashboard-only data loads
  // (get-daily-briefing, calendar-list-events) on every non-dashboard landing. The URL-restore
  // effect below still runs and stays the source of truth; this just picks the right starting view.
  const [activeView, setActiveView] = useState(() => {
    if (typeof window === 'undefined') return 'dashboard';
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    if (path === '/integrations') return 'integrations';
    if (path === '/settings') return 'settings';
    if (path === '/org-settings') return 'org_settings';
    if (path === '/meetings' || path.startsWith('/meetings/')) return 'meetings';
    if (path === '/action-items') return 'action_items';
    if (path === '/releases') return 'releases';
    if (path === '/feedback') return 'feedback';
    if (path === '/leaderboard') return 'leaderboard';
    if (path === '/admin' || isAdminHost()) return 'admin';
    return 'dashboard';
  });
  const [personalSection, setPersonalSection] = useState('preferences');
  const [orgSection, setOrgSection] = useState('general');
  /** Pre-applied age filter when drilling into Action items from the dashboard age chart (DR-13). */
  const [actionsInitialAgeBucket, setActionsInitialAgeBucket] = useState<string | null>(null);
  /** The backlog banner sent the user to Action items to run the swipe cleaner. */
  const [actionsOpenCleaner, setActionsOpenCleaner] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState<'success' | 'canceled' | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);
  /** Sidebar "Enrol meeting" - open Meetings in enrolment flow once consumed by <Meetings />. */
  const [meetingsOpenEnrollFromNav, setMeetingsOpenEnrollFromNav] = useState(false);
  /** Bumps when sidebar "Meetings" is chosen - resets <Meetings /> to list (e.g. leave Add flow). */
  const [meetingsListNavTick, setMeetingsListNavTick] = useState(0);
  /** Dashboard (etc.) opens a specific meeting instance; `seq` forces re-run when clicking again. */
  const [meetingsOpenSessionRequest, setMeetingsOpenSessionRequest] = useState<{
    meetingId: string;
    seq: number;
    preferredTab?: 'agenda' | 'checkins' | 'summary' | 'actions' | 'decisions' | 'people' | 'transcript';
  } | null>(null);

  const { workspace, company, membership, isLoading, error, refreshWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { isTrialExpired, isLoading: isTrialLoading } = useTrialStatus();
  const isAdmin = membership?.role === 'admin';

  // Seamless calendar: persist the Google/Microsoft sign-in token into calendar_connections as soon
  // as a workspace is active, so users never need a separate "Connect calendar" step. No-ops when a
  // connection already exists or the sign-in carried no provider tokens.
  useEffect(() => {
    if (workspace?.id && user?.id) {
      void ensureCalendarConnectionFromAuth({ workspaceId: workspace.id, userId: user.id });
    }
  }, [workspace?.id, user?.id]);

  const clearMeetingsOpenSessionRequest = useCallback(() => setMeetingsOpenSessionRequest(null), []);

  const openMeetingSessionFromDashboard = useCallback((meetingId: string) => {
    setMeetingsOpenSessionRequest((prev) => ({
      meetingId,
      seq: (prev?.seq ?? 0) + 1,
    }));
    setActiveView('meetings');
  }, []);

  const openNewMeetingWizard = useCallback(() => {
    setMeetingsOpenEnrollFromNav(false);
    setActiveView('meetings');
    window.requestAnimationFrame(() => {
      setMeetingsOpenEnrollFromNav(true);
    });
  }, []);

  const openActionItems = useCallback((ageBucket?: string) => {
    // Guard against callers wired directly to onClick (which would pass the event object as the
    // bucket); only a real string age-bucket should become the filter.
    setActionsInitialAgeBucket(typeof ageBucket === 'string' ? ageBucket : null);
    setActiveView('action_items');
  }, []);

  /** Backlog banner CTA: land on an unfiltered list with the cleaner already open. */
  const openActionItemCleaner = useCallback(() => {
    setActionsInitialAgeBucket(null);
    setActionsOpenCleaner(true);
    setActiveView('action_items');
  }, []);

  const navigateToMeetingFromSearch = useCallback(
    (
      meetingId: string,
      preferredTab?: 'agenda' | 'checkins' | 'summary' | 'actions' | 'decisions' | 'people' | 'transcript',
    ) => {
      setMeetingsOpenSessionRequest((prev) => ({
        meetingId,
        seq: (prev?.seq ?? 0) + 1,
        preferredTab,
      }));
      setActiveView('meetings');
    },
    [],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'checkout') {
      setCheckoutResult('success');
      setActiveView('org_settings');
      setOrgSection('billing');
      window.history.replaceState({}, '', window.location.pathname || '/');
    } else if (params.get('canceled') === 'true') {
      setCheckoutResult('canceled');
      setActiveView('org_settings');
      setOrgSection('billing');
      window.history.replaceState({}, '', window.location.pathname || '/');
    } else if (params.get('code') && params.get('state')) {
      const path = window.location.pathname.replace(/\/$/, '') || '/';
      if (path === '/integrations') {
        setActiveView('integrations');
      } else {
        setActiveView('integrations');
      }
    }
  }, []);

  useEffect(() => {
    if (isLoading) return;
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    if (path === '/integrations') {
      setActiveView('integrations');
    } else if (path === '/settings') {
      setActiveView('settings');
    } else if (path === '/org-settings') {
      setActiveView('org_settings');
    } else if (path === '/meetings') {
      setActiveView('meetings');
      // Honour ?meeting=<id> so external/redirected links open the specific occurrence.
      const requested = new URLSearchParams(window.location.search).get('meeting');
      if (requested) {
        openMeetingSessionFromDashboard(requested);
        // Strip the query param now that we've consumed it - the meetings view manages its
        // own URL state below.
        window.history.replaceState({}, '', '/meetings');
      }
    } else if (path === '/action-items') {
      setActiveView('action_items');
    } else if (path === '/releases') {
      setActiveView('releases');
    } else if (path === '/feedback') {
      setActiveView('feedback');
    } else if (path === '/leaderboard') {
      setActiveView('leaderboard');
    } else if (path === '/admin') {
      setActiveView('admin');
    } else if (/^\/meetings\/series\/[^/]+\/timeline$/.test(path)) {
      window.history.replaceState({}, '', '/meetings');
      setActiveView('meetings');
    }
  }, [isLoading, openMeetingSessionFromDashboard]);

  useEffect(() => {
    if (isLoading || !workspace) return;
    let path = '/';
    if (activeView === 'integrations') path = '/integrations';
    else if (activeView === 'settings') path = '/settings';
    else if (activeView === 'org_settings') path = '/org-settings';
    else if (activeView === 'meetings') path = '/meetings';
    else if (activeView === 'action_items') path = '/action-items';
    else if (activeView === 'releases') path = '/releases';
    else if (activeView === 'feedback') path = '/feedback';
    else if (activeView === 'leaderboard') path = '/leaderboard';
    else if (activeView === 'admin') path = '/admin';
    // Preserve query params for views that own their sub-state in the URL (settings tabs;
    // meetings opens a specific meeting via ?meeting=...).
    const search = activeView === 'settings' || activeView === 'org_settings' || activeView === 'meetings' ? window.location.search : '';
    const desired = path + search;
    if (window.location.pathname + window.location.search !== desired) {
      // pushState (not replaceState) so the browser back button moves between in-app views
      // instead of escaping the SPA back to the OAuth callback URL and dumping the user on login.
      window.history.pushState({}, '', desired);
    }
  }, [isLoading, workspace, activeView]);

  // Back/forward: re-sync activeView from the URL. Also defensively scrub any stale OAuth/auth
  // params if the back navigation lands on the original sign-in callback URL - re-processing a
  // consumed code/token throws the user back to login.
  useEffect(() => {
    const handler = () => {
      const search = window.location.search;
      const hash = window.location.hash;
      const looksLikeStaleAuth =
        search.includes('code=') ||
        search.includes('access_token=') ||
        hash.includes('access_token=') ||
        hash.includes('type=invite') ||
        hash.includes('type=recovery');
      if (looksLikeStaleAuth) {
        window.history.replaceState({}, '', window.location.pathname);
      }
      const path = window.location.pathname.replace(/\/$/, '') || '/';
      if (path === '/integrations') setActiveView('integrations');
      else if (path === '/settings') setActiveView('settings');
      else if (path === '/org-settings') setActiveView('org_settings');
      else if (path === '/meetings') setActiveView('meetings');
      else if (path === '/action-items') setActiveView('action_items');
      else if (path === '/releases') setActiveView('releases');
      else if (path === '/leaderboard') setActiveView('leaderboard');
      else if (path === '/admin') setActiveView('admin');
      else setActiveView('dashboard');
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  useEffect(() => {
    if (workspace && membership) {
      loadUserProfile(membership.user_id);
    }
  }, [workspace, membership]);

  const loadUserProfile = async (userId: string) => {
    const { data, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (!profileError && data) {
      setUserProfile({
        ...data,
        role: membership?.role || 'member',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-background p-6">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded"
          >
            Return to Workspace Selection
          </button>
        </div>
      </div>
    );
  }

  const oauthCallbackParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const isOAuthReturn = Boolean(
    oauthCallbackParams?.get('code') && oauthCallbackParams?.get('state'),
  );

  const showWorkspaceSetupWizard =
    isAdmin &&
    workspace &&
    !isWorkspaceSetupComplete(workspace.settings) &&
    !isOAuthReturn;

  const isOnBillingPage = activeView === 'org_settings' && orgSection === 'billing';
  if (!isTrialLoading && isTrialExpired && !(isAdmin && isOnBillingPage)) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="max-w-md text-center p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Free Trial Expired</h1>
          <p className="text-muted-foreground mb-6">
            Your 30-day free trial has ended. Subscribe to a plan to continue.
          </p>
          {isAdmin ? (
            <button
              type="button"
              onClick={() => {
                setActiveView('org_settings');
                setOrgSection('billing');
              }}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors mb-3"
            >
              Subscribe Now
            </button>
          ) : (
            <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md mb-3">
              Contact your workspace admin to subscribe to a plan.
            </p>
          )}
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={onSwitchWorkspace}
              className="px-4 py-2 text-sm border rounded-md hover:bg-muted transition-colors"
            >
              Switch Workspace
            </button>
            <button
              type="button"
              onClick={onSignOut}
              className="px-4 py-2 text-sm border rounded-md hover:bg-muted transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
        <Toaster />
      </div>
    );
  }

  if (showWorkspaceSetupWizard) {
    return (
      <>
        <WorkspaceOnboardingWizard
          onComplete={() => {
            refreshWorkspace();
          }}
          onSignOut={onSignOut}
          onSwitchWorkspace={onSwitchWorkspace}
        />
        <Toaster />
      </>
    );
  }

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <Dashboard
            userProfile={userProfile}
            workspaceName={company?.name || workspace?.name}
            workspaceId={workspace?.id}
            onNavigateToMeetings={() => setActiveView('meetings')}
            onNavigateToIntegrations={() => setActiveView('integrations')}
            onNewMeeting={openNewMeetingWizard}
            onNavigateToActionItems={openActionItems}
            onOpenMeetingSession={openMeetingSessionFromDashboard}
            onSignOut={onSignOut}
            onSwitchWorkspace={onSwitchWorkspace}
            onHelp={() => {
              setActiveView('org_settings');
              setOrgSection('general');
            }}
          />
        );
      case 'integrations':
        return <Integrations />;
      case 'meetings':
        return (
          <Meetings
            userProfile={userProfile}
            openEnrollFromNav={meetingsOpenEnrollFromNav}
            onConsumedOpenEnrollFromNav={() => setMeetingsOpenEnrollFromNav(false)}
            meetingsListNavTick={meetingsListNavTick}
            openSessionForMeetingId={meetingsOpenSessionRequest}
            onConsumedOpenSessionForMeetingId={clearMeetingsOpenSessionRequest}
            onNavigateToDashboard={() => setActiveView('dashboard')}
            onSignOut={onSignOut}
            onSwitchWorkspace={onSwitchWorkspace}
            onHelp={() => {
              setActiveView('org_settings');
              setOrgSection('general');
            }}
            onSupport={() => {
              setActiveView('org_settings');
              setOrgSection('general');
            }}
          />
        );
      case 'releases':
        return <ReleasesPage />;
      case 'feedback':
        return <FeedbackPage />;
      case 'leaderboard':
        // Old bookmark path - the leaderboard now lives inside the admin dashboard.
        return <AdminDashboard initialTab="leaderboard" />;
      case 'admin':
        return <AdminDashboard />;
      case 'action_items':
        return (
          <ActionItemsPage
            userProfile={userProfile}
            initialAgeBucket={actionsInitialAgeBucket}
            openCleanerOnMount={actionsOpenCleaner}
            onConsumedOpenCleaner={() => setActionsOpenCleaner(false)}
            onSignOut={onSignOut}
            onSwitchWorkspace={onSwitchWorkspace}
            onHelp={() => {
              setActiveView('org_settings');
              setOrgSection('general');
            }}
          />
        );
      case 'settings':
        return (
          <SettingsNavigation
            mode="personal"
            activeSettingsView={personalSection}
            setActiveSettingsView={setPersonalSection}
            userProfile={userProfile}
          />
        );
      case 'org_settings':
        return (
          <SettingsNavigation
            mode="org"
            activeSettingsView={orgSection}
            setActiveSettingsView={setOrgSection}
            userProfile={userProfile}
            checkoutResult={checkoutResult}
            onCheckoutResultClose={() => setCheckoutResult(null)}
          />
        );
      default:
        return (
          <Dashboard
            userProfile={userProfile}
            workspaceName={company?.name || workspace?.name}
            workspaceId={workspace?.id}
            onNavigateToMeetings={() => setActiveView('meetings')}
            onNavigateToIntegrations={() => setActiveView('integrations')}
            onNewMeeting={openNewMeetingWizard}
            onNavigateToActionItems={openActionItems}
            onOpenMeetingSession={openMeetingSessionFromDashboard}
            onSignOut={onSignOut}
            onSwitchWorkspace={onSwitchWorkspace}
            onHelp={() => {
              setActiveView('org_settings');
              setOrgSection('general');
            }}
          />
        );
    }
  };

  return (
    <WorkspaceSearchProvider
      workspaceId={workspace?.id}
      workspaceName={company?.name || workspace?.name}
      userDisplayName={userProfile?.full_name}
      onNavigateToMeeting={navigateToMeetingFromSearch}
    >
      <AgentChatProvider
        workspaceId={workspace?.id}
        onNavigateToMeeting={openMeetingSessionFromDashboard}
      >
      <div className="flex h-screen bg-background">
        <Sidebar
          activeView={activeView}
          onNavigate={(view) => {
            if (view === 'meetings') {
              setMeetingsListNavTick((t) => t + 1);
            }
            if (view === 'action_items') {
              setActionsInitialAgeBucket(null);
            }
            setActiveView(view);
          }}
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          userProfile={userProfile}
          isStaff={Boolean(userProfile?.is_staff)}
          onSignOut={onSignOut}
          onSwitchWorkspace={onSwitchWorkspace}
          workspaceName={company?.name || workspace?.name}
          onHelp={() => {
            setActiveView('org_settings');
            setOrgSection('general');
          }}
        />
        <div
          className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}
        >
          {/* Skipped on the Action items page itself, which already has a Clean up button. */}
          {activeView !== 'action_items' ? (
            <ActionItemBacklogBanner
              workspaceId={workspace?.id}
              userId={userProfile?.id}
              workspaceName={company?.name || workspace?.name}
              onStartCleanup={openActionItemCleaner}
            />
          ) : null}
          <main
            className={`flex-1 overflow-hidden min-w-0 ${
              activeView === 'settings' || activeView === 'org_settings'
                ? ''
                : activeView === 'meetings' || activeView === 'dashboard' || activeView === 'action_items'
                  ? 'px-4 pb-4 overflow-y-auto'
                  : 'p-4 overflow-y-auto'
            }`}
          >
            {renderView()}
          </main>
        </div>
        <Toaster />
      </div>
      </AgentChatProvider>
    </WorkspaceSearchProvider>
  );
}

function AppInner() {
  const { session, user, authState, setAuthState, signOut } = useAuth();
  const { refreshInvitations } = useInvitations();
  const [currentView, setCurrentView] = useState<View | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  // True when the user explicitly asked to change workspace (sidebar Switch workspace) - the
  // selection screen then always shows the picker instead of auto-selecting a lone membership.
  const [switchIntent, setSwitchIntent] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  // Bumped to force a re-render after a client-side route rewrite (see handleAttendeeMemberHandoff).
  const [, setRouteTick] = useState(0);

  // A full workspace member landed on /meet/direct/:slug. Instead of a full page reload into the
  // main app (a second boot), rewrite the URL to /meetings?meeting=… and re-render so the
  // /meet/direct interception below no longer matches - we fall through to the normal app shell,
  // whose URL handler focuses the occurrence.
  const handleAttendeeMemberHandoff = useCallback((meetingId: string | null) => {
    window.history.replaceState(
      {},
      '',
      meetingId ? `/meetings?meeting=${encodeURIComponent(meetingId)}` : '/meetings',
    );
    setRouteTick((n) => n + 1);
  }, []);

  useEffect(() => {
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    if (path.startsWith('/meet/direct/')) return;
    const isAuthedLike =
      authState === 'authenticated' ||
      authState === 'loading_workspace' ||
      authState === 'selecting_workspace' ||
      authState === 'checking_invitations';
    if (!isAuthedLike) return;

    const pendingSlug = localStorage.getItem(PENDING_ATTENDEE_SLUG_KEY);
    if (!pendingSlug) return;

    window.location.replace(`/meet/direct/${encodeURIComponent(pendingSlug)}`);
  }, [authState]);

  useEffect(() => {
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.substring(1));
    const type = hashParams.get('type');

    if (type === 'recovery') {
      setShowPasswordReset(true);
    }
  }, []);

  useEffect(() => {
    const route = async () => {
      if (authState === 'authenticated' && currentView === 'workspace' && selectedWorkspaceId) {
        return;
      }

      if (authState === 'mfa_challenge') {
        setCurrentView('mfa_challenge');
        return;
      }

      switch (authState) {
        case 'initializing':
          setCurrentView(null);
          break;

        case 'unauthenticated':
          setCurrentView('landing');
          setSelectedWorkspaceId(null);
          break;

        case 'authenticating':
          setCurrentView(null);
          break;

        case 'checking_invitations':
          if (currentView === 'workspace' && selectedWorkspaceId) {
            setAuthState('authenticated');
            break;
          }

          try {
            const invitations = await refreshInvitations();
            const hasInvitations = invitations.length > 0;

            if (hasInvitations) {
              setAuthState('accepting_invitation');
            } else {
              setAuthState('selecting_workspace');
            }
          } catch {
            setAuthState('selecting_workspace');
          }
          break;

        case 'accepting_invitation':
          setCurrentView('supabase-invitation');
          break;

        case 'selecting_workspace':
          if (selectedWorkspaceId && currentView === 'workspace') {
            setAuthState('authenticated');
            break;
          }

          if (currentView === 'workspace-select') {
            break;
          }

          try {
            if (user) {
              const lastWorkspaceId = await getLastActiveWorkspace();
              if (lastWorkspaceId) {
                setSelectedWorkspaceId(lastWorkspaceId);
                setAuthState('loading_workspace');
              } else {
                setCurrentView('workspace-select');
              }
            } else {
              setCurrentView('workspace-select');
            }
          } catch {
            setCurrentView('workspace-select');
          }
          break;

        case 'loading_workspace':
          if (selectedWorkspaceId) {
            setCurrentView('workspace');
            setAuthState('authenticated');
          } else {
            setAuthState('selecting_workspace');
          }
          break;

        case 'authenticated':
          if (selectedWorkspaceId) {
            if (currentView !== 'workspace') {
              setCurrentView('workspace');
            }
          } else {
            setCurrentView('workspace-select');
          }
          break;
      }
    };

    route();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, user, selectedWorkspaceId]);

  // Escape hatch: ?switch=<workspaceId> bypasses last-active and loads that workspace directly
  useEffect(() => {
    if (authState !== 'authenticated') return;
    const switchParam = new URLSearchParams(window.location.search).get('switch');
    if (switchParam) {
      window.history.replaceState({}, '', '/');
      handleWorkspaceSelected(switchParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  const handleInvitationAccepted = useCallback(async () => {
    await refreshInvitations();

    if (user) {
      try {
        const lastWorkspaceId = await getLastActiveWorkspace();
        if (lastWorkspaceId) {
          setSelectedWorkspaceId(lastWorkspaceId);
          setAuthState('loading_workspace');
        } else {
          setAuthState('selecting_workspace');
        }
      } catch {
        setAuthState('selecting_workspace');
      }
    } else {
      setAuthState('selecting_workspace');
    }
  }, [user, refreshInvitations, setAuthState]);

  const handleWorkspaceSelected = useCallback(
    async (workspaceId: string) => {
      if (user) {
        try {
          await updateLastActiveWorkspace(workspaceId);
          setSwitchIntent(false);
          setSelectedWorkspaceId(workspaceId);
          setAuthState('loading_workspace');
        } catch {
          toast.error('Failed to switch workspace');
        }
      }
    },
    [user, setAuthState],
  );

  const handleSignOut = useCallback(async () => {
    await signOut();
    setSwitchIntent(false);
    setSelectedWorkspaceId(null);
    setCurrentView('landing');
  }, [signOut]);

  // Unauthenticated unsubscribe / preferences page (signed-token).
  const unsubscribePath = window.location.pathname.replace(/\/$/, '');
  if (unsubscribePath === '/unsubscribe') {
    return <UnsubscribePage />;
  }

  // Hidden design-system reference (unlisted; used for the Figma migration).
  if (unsubscribePath === '/design-system') {
    return <DesignSystemPage />;
  }

  // Unauthenticated approve/cancel recap action page (signed-token).
  if (unsubscribePath === '/approve-recap') {
    return <ApproveRecapPage />;
  }

  // Unauthenticated "add to agenda" landing page driven by pre-meeting nudge links (signed-token).
  if (unsubscribePath === '/agenda/add') {
    return <AddAgendaPage />;
  }

  // Unauthenticated "marked done" confirmation page - the action-item-action edge function does
  // the mutation server-side then 302s here so React renders the confirmation reliably (some
  // email webviews mangle a direct text/html response from the function).
  if (unsubscribePath === '/action-item/done') {
    return <ActionItemDonePage />;
  }

  // Unauthenticated "add update" page for the per-item links in nudge/recap emails (signed-token).
  if (unsubscribePath === '/action-item/update') {
    return <ActionItemUpdatePage />;
  }

  // OAuth 2.1 consent page - GoTrue's oauth_server_authorization_path lands here with
  // ?authorization_id= when an AI client (MCP) asks to connect. Needs an app session, which the
  // page handles itself.
  if (unsubscribePath === '/oauth/consent') {
    return <OAuthConsentPage />;
  }

  // Intercept /meet/direct/:slug before the workspace auth gate
  const attendeeSlugMatch = window.location.pathname.match(/^\/meet\/direct\/([^/]+)\/?$/);
  if (attendeeSlugMatch) {
    return (
      <AttendeeGate
        slug={decodeURIComponent(attendeeSlugMatch[1])}
        onMemberHandoff={handleAttendeeMemberHandoff}
      />
    );
  }

  // Attendee portal landing - list all meetings for the signed-in attendee
  const attendeePortalPath = window.location.pathname.replace(/\/$/, '');
  if (attendeePortalPath === '/meet') {
    return <AttendeeAllMeetings />;
  }

  // Recovery links must show the password-reset form regardless of auth/session state, so this
  // wins ahead of the boot gate below (a recovery hash also restores a session, which would
  // otherwise leave us mid-resolution on the loader).
  if (showPasswordReset) {
    return (
      <>
        <PasswordReset
          onSuccess={() => {
            setShowPasswordReset(false);
            setAuthState('unauthenticated');
          }}
          onCancel={() => {
            setShowPasswordReset(false);
            setAuthState('unauthenticated');
          }}
        />
        <Toaster />
      </>
    );
  }

  // DR-22 - unified boot gate. `authState` and `currentView` are separate atoms updated across
  // several async hops (invitations check + last-active-workspace lookup), so a stale `currentView`
  // would otherwise paint the previous screen mid-transition - most visibly the sign-in form
  // lingering for a beat after login, or a flash of the workspace picker. While the machine is
  // mid-flight we show ONE loader; a real screen only paints once the two agree on a settled
  // destination (landing / mfa / invitation / picker / workspace).
  // Once a workspace is mounted, background auth churn must NOT drop us to the boot loader:
  // Supabase re-fires SIGNED_IN on tab focus / token refresh, which briefly bounces authState
  // through `checking_invitations`. If we showed the loader for that, WorkspaceProvider would
  // unmount and reload the entire workspace (membership → workspace → company → profile) on every
  // such event. The boot gate is only for the INITIAL resolve, before any workspace is rendered.
  const workspaceMounted = currentView === 'workspace' && !!selectedWorkspaceId;

  const isResolvingSession =
    !workspaceMounted &&
    (authState === 'initializing' ||
      authState === 'authenticating' ||
      authState === 'checking_invitations' ||
      authState === 'loading_workspace' ||
      (authState === 'mfa_challenge' && currentView !== 'mfa_challenge') ||
      (authState === 'accepting_invitation' &&
        currentView !== 'supabase-invitation' &&
        currentView !== 'invitation') ||
      (authState === 'selecting_workspace' && currentView !== 'workspace-select') ||
      (authState === 'authenticated' &&
        currentView !== 'workspace' &&
        currentView !== 'workspace-select'));

  if (isResolvingSession || (currentView === null && authState !== 'unauthenticated')) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (currentView === 'mfa_challenge') {
    return (
      <>
        <MfaChallenge onVerified={() => {}} />
        <Toaster />
      </>
    );
  }

  if (currentView === 'supabase-invitation') {
    return (
      <>
        <AcceptInvitation
          onAccepted={handleInvitationAccepted}
          onError={() => {
            setAuthState('selecting_workspace');
          }}
        />
        <Toaster />
      </>
    );
  }

  if (currentView === 'workspace-select') {
    return (
      <>
        <WorkspaceSelect
          mode={switchIntent ? 'switch' : 'login'}
          onSelectWorkspace={handleWorkspaceSelected}
          onSignOut={handleSignOut}
        />
        <Toaster />
      </>
    );
  }

  if (currentView === 'workspace' && selectedWorkspaceId) {
    return (
      <WorkspaceProvider workspaceId={selectedWorkspaceId}>
        <AppContent
          onSwitchWorkspace={() => {
            setSwitchIntent(true);
            setCurrentView('workspace-select');
            setSelectedWorkspaceId(null);
            setAuthState('selecting_workspace');
          }}
          onSignOut={handleSignOut}
        />
      </WorkspaceProvider>
    );
  }

  return (
    <>
      <Auth onAuthenticated={() => {}} />
      <Toaster />
    </>
  );
}

export default function App() {
  return <AppInner />;
}
